import React, { useState, useEffect } from 'react'
import './MiscRecordsFieldDefinitionSettings.scss'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import CrossHeavy from '@tecuity/sos-icons/react/cross-heavy'
import TrashHeavy from '@tecuity/sos-icons/react/trash-heavy'
import { Spinner } from '../../Loaders/Loaders'
import Portal from 'components/Portal/Portal'
import { ConfirmDialog } from '../../Modal/Modal'
import { noop } from 'utilities'
import * as api from '../../../requests'
import { setErrorToast, setSuccessToast } from '../../../actions'
import { SUBROUTES } from '../../../App'
import FieldDefinitionForm from './FieldDefinitionForm'

const MiscRecordsFieldDefinitionSettings = () => {
  const dispatch = useDispatch()
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const readOnly = !appUserRights.includes('CanConfigureMiscellaneousModules')

  const allModules = (SUBROUTES || [])
    .filter((r) => r.IsMiscRecordsType)
    .flatMap((r) => r.MiscRecordsModules || [])
    .sort((a, b) => a.DisplayName.localeCompare(b.DisplayName))

  const moduleOptions = allModules.map((m) => ({
    value: m.MiscellaneousModuleId,
    label: m.DisplayName,
  }))

  const [selectedModuleId, setSelectedModuleId] = useState(null)
  const [fieldSummaries, setFieldSummaries] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [selectedFieldId, setSelectedFieldId] = useState(null)
  const [selectedFieldDetail, setSelectedFieldDetail] = useState(null)
  const [fieldToDelete, setFieldToDelete] = useState(null)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [dataTypeOptions, setDataTypeOptions] = useState([])

  useEffect(() => {
    if (allModules.length > 0) {
      setSelectedModuleId(allModules[0].MiscellaneousModuleId)
    }
  }, [])

  useEffect(() => {
    if (!selectedModuleId) return
    setFieldSummaries([])
    setSelectedFieldId(null)
    setSelectedFieldDetail(null)
    setIsLoading(true)
    api.getMiscRecordsFieldDefinitions(
      selectedModuleId,
      (res) => {
        setFieldSummaries(res)
        setIsLoading(false)
      },
      () => {
        dispatch(setErrorToast('Failed to load field definitions.'))
        setIsLoading(false)
      },
    )
  }, [selectedModuleId])

  useEffect(() => {
    api.getMiscRecordsFieldDataTypes(
      (res) => {
        setDataTypeOptions(res.map((t, i) => ({ value: i, label: t })))
      },
      () => {
        dispatch(setErrorToast('Failed to load data types.'))
      },
    )
  }, [])

  const handleSelectField = (miscellaneousFieldId) => {
    setIsDetailLoading(true)
    api.getMiscRecordsFieldDefinition(
      miscellaneousFieldId,
      (res) => {
        setSelectedFieldId(miscellaneousFieldId)
        setSelectedFieldDetail(res)
        setIsDetailLoading(false)
      },
      () => {
        dispatch(setErrorToast('Failed to load field definition.'))
        setIsDetailLoading(false)
      },
    )
  }

  const handleAddNew = () => {
    setSelectedFieldId(null)
    setSelectedFieldDetail(null)
  }

  const handleSave = (savedField) => {
    const { MiscellaneousFieldId, FieldDisplayName } = savedField
    setFieldSummaries((prev) => {
      const existingIndex = prev.findIndex((s) => s.MiscellaneousFieldId === MiscellaneousFieldId)
      if (existingIndex >= 0) {
        const updated = [...prev]
        updated[existingIndex] = { MiscellaneousFieldId, FieldDisplayName }
        return updated
      }
      return [...prev, { MiscellaneousFieldId, FieldDisplayName }]
    })
    setSelectedFieldId(MiscellaneousFieldId)
    setSelectedFieldDetail(savedField)
  }

  const handleDeleteConfirm = () => {
    if (!fieldToDelete) return
    api.deleteMiscRecordsFieldDefinition(
      fieldToDelete.MiscellaneousFieldId,
      () => {
        setFieldSummaries((prev) =>
          prev.filter((s) => s.MiscellaneousFieldId !== fieldToDelete.MiscellaneousFieldId),
        )
        if (selectedFieldId === fieldToDelete.MiscellaneousFieldId) {
          setSelectedFieldId(null)
          setSelectedFieldDetail(null)
        }
        dispatch(setSuccessToast('Field definition deleted successfully.'))
        setIsDeleteConfirmOpen(false)
        setFieldToDelete(null)
      },
      () => {
        dispatch(setErrorToast('Failed to delete field definition.'))
        setIsDeleteConfirmOpen(false)
        setFieldToDelete(null)
      },
    )
  }

  return (
    <div className="misc-records-field-definition-settings">
      <h2>Miscellaneous Field Definitions</h2>
      <div className="misc-records-field-definition-settings__content">
        <div className="misc-records-field-definition-settings__list-panel">
          <SelectBox
            label="Module"
            noPlaceholder
            value={selectedModuleId || ''}
            onChange={(val) => setSelectedModuleId(val)}
            options={moduleOptions}
          />
          <TextInput
            customCss={`input {border: none}`}
            placeholder="Filter"
            onChange={(e) => setFilterText(e.target.value)}
          />
          <div className="misc-records-field-definition-settings__list">
            <div className="misc-records-field-definition-settings__list-scroll">
              {isLoading ? (
                <Spinner />
              ) : fieldSummaries.length === 0 ? (
                <div>No field definitions configured.</div>
              ) : (
                fieldSummaries
                  .filter((s) => s.FieldDisplayName.toLowerCase().includes(filterText.toLowerCase()))
                  .map((summary) => (
                    <div
                      key={summary.MiscellaneousFieldId}
                      className={`misc-records-field-definition-settings__list-item${
                        selectedFieldId === summary.MiscellaneousFieldId ? ' selected' : ''
                      }`}
                      onClick={() => handleSelectField(summary.MiscellaneousFieldId)}
                    >
                      <span>{summary.FieldDisplayName}</span>
                      {!readOnly && (
                        <div
                          className="misc-records-field-definition-settings__delete-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            setFieldToDelete(summary)
                            setIsDeleteConfirmOpen(true)
                          }}
                        >
                          <TrashHeavy />
                        </div>
                      )}
                    </div>
                  ))
              )}
            </div>
            {!readOnly && (
              <ContextButton variant="secondary" onClick={handleAddNew}>
                <CrossHeavy />
              </ContextButton>
            )}
          </div>
        </div>

        <div className="misc-records-field-definition-settings__form-panel">
          {isDetailLoading ? (
            <Spinner size="large" themeColor="dark-neutral" />
          ) : (
            <FieldDefinitionForm
              key={`${selectedModuleId}-${selectedFieldId}`}
              moduleId={selectedModuleId}
              fieldDefinition={selectedFieldDetail}
              onSave={handleSave}
              onCancel={() => {
                setSelectedFieldId(null)
                setSelectedFieldDetail(null)
              }}
              dataTypeOptions={dataTypeOptions}
              readOnly={readOnly}
            />
          )}
        </div>
      </div>

      <Portal isOpened={isDeleteConfirmOpen}>
        <ConfirmDialog
          closeModal={() => {
            setIsDeleteConfirmOpen(false)
            setFieldToDelete(null)
          }}
          onModalClosed={noop}
          message="Are you sure you want to delete this field definition?"
          buttons={[
            { label: 'No', onClick: noop, className: 'btn-default' },
            { label: 'Yes', onClick: handleDeleteConfirm, className: 'btn-danger' },
          ]}
        />
      </Portal>
    </div>
  )
}

export default MiscRecordsFieldDefinitionSettings
